import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setChallenge } from '../../../redux/actions/challenges'
import { dateToDisplayDateOrEmpty, isEmptyNote } from '../../../utils/helpers'
import ExternalImage from '../shared/ExternalImage'
import ReactHtmlParser from 'react-html-parser'

import QuestionAnswerList from '../shared/Questions/QuestionAnswerList'
import Tabs from '../shared/Tabs'
import Tab from '../shared/Tabs/Tab'
import TeamFormationTeamRequirementsForm from '../team-formation/TeamFormationTeamRequirementsForm'
import Modal from 'components/shared/Modal'
import AvatarUserInfo from '../shared/AvatarUserInfo'
import { useAuth } from 'utils/auth'

interface ChallengeDetailsModalProps {
	challengeId: string
	isOpen?: boolean
	onClose?: Function
	onEdit?: Function
	hideTabs?: Array<string>
	initialTab?: string
}

const ChallengeDetailsModal = (props: ChallengeDetailsModalProps) => {
	const { onClose, onEdit, isOpen, challengeId, hideTabs, initialTab } = props

	const auth = useAuth()
	const [selectedTab, setSelectedTab] = useState(initialTab)
	const challenge = useSelector((state) => state['account'].selectedChallenge)
	const challengeTeamRequirements = useSelector(
		(state) => state['account'].selectedChallengeTeamRequirements
	)
	const companies = useSelector((state) => state['account'].company)
	const addresses = useSelector((state) => state['account'].address)
	const users = useSelector((state) => state['account'].user)
	const organisations = useSelector((state) => {
		return Object.keys(state['account']?.organisation).length
			? state['account']?.organisation
			: state['auth']?.organisation
	})

	const { questions, answers } = useSelector((state) => state['account'])

	const dispatch = useDispatch()

	useEffect(() => {
		if (challengeId) {
			dispatch(
				setChallenge(challengeId, {
					include: 'teamRequirements,answers,answers.question',
				})
			)
		}
	}, [])

	let challengeOwners = null
	if (challenge?.relationships) {
		challengeOwners = challenge.relationships.challengeUsers.data.length
			? challenge.relationships.challengeUsers.data.map(
					(owner) => users[owner.id]
			  )
			: null
	}

	let entity = null
	let entityAddress = null
	if (challenge?.attributes?.isInternalChallenge) {
		entity = organisations[challenge?.attributes?.organisationId]
	} else {
		entity = companies[challenge?.relationships?.company?.data?.id]
	}

	if (entity?.relationships) {
		entityAddress = addresses[entity.relationships.address.data.id]
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			contentLabel={challenge?.attributes?.title}
			headerClass={'bg-yellow-300'}
			contentClass={''}
		>
			{challenge?.attributes && (
				<Tabs
					initialTab={selectedTab ?? 'details'}
					hideTabs={hideTabs}
					onTabChange={setSelectedTab}
				>
					<Tab tabKey="details" title="Details">
						<p className="text-gray-700 whitespace-pre-wrap mb-4 p-4 border-b">
							{challenge?.attributes?.description}
						</p>
						<div className="px-4 mb-8">
							<span className="uppercase whitespace-nowrap block font-bold text-sm mb-1">
								Questions and answers
							</span>
							<div className="space-y-2">
								{answers.length > 0 ? (
									<QuestionAnswerList questions={questions} answers={answers} />
								) : (
									<p className="text-gray-700">No extra info yet</p>
								)}
							</div>
						</div>
					</Tab>

					<Tab tabKey="team_requirements" title="Team requirements">
						<div className="px-4">
							<TeamFormationTeamRequirementsForm
								challengeId={challenge.id}
								challengeSpaceId={
									challenge.relationships?.challengeSpace?.data?.id
								}
								requirements={challengeTeamRequirements}
								viewOnly
							/>
						</div>
					</Tab>
					<Tab tabKey="company_info" title="Company info">
						{entity && (
							<div className="flex items-center p-4 w-full">
								{entity.attributes?.logoUrl && (
									<div className="w-16 h-16 mr-4 p-1 rounded border relative bg-white flex-shrink-0">
										<ExternalImage
											url={entity.attributes.logoUrl}
											className="object-contain object-center block w-full h-full"
										/>
									</div>
								)}
								<div className="flex-grow">
									<h2 className="text-xl font-sans font-bold">
										{entity.attributes.name}
										{entityAddress && (
											<small className="block">
												<span className="font-sans font-normal text-gray-600">
													{entityAddress.attributes.streetName}{' '}
													{entityAddress.attributes.houseNumber}
													{entityAddress.attributes.houseNumberExtension},{' '}
													{entityAddress.attributes.postalCode}{' '}
													{entityAddress.attributes.cityName}
												</span>
											</small>
										)}
									</h2>
								</div>
							</div>
						)}

						{entity && challengeOwners && challengeOwners.length > 0 && (
							<div className="flex flex-wrap px-4 pt-4">
								<div className="w-full sm:w-1/2 mb-4">
									<span className="uppercase whitespace-nowrap block font-bold text-sm mb-2">
										Contact persons
									</span>
									{challengeOwners
										.filter((a) => Boolean(a))
										.map((challengeOwner) =>
											challengeOwner ? (
												<div
													className="flex flex-no-wrap items-center w-full"
													key={challengeOwner.id}
												>
													<AvatarUserInfo
														user={challengeOwner}
														showRole={true}
													/>
												</div>
											) : null
										)}
								</div>
								{entity?.attributes?.websiteUrl && (
									<div className="w-full sm:w-1/2 mb-4">
										<span className="uppercase whitespace-nowrap block font-bold text-sm mb-1">
											Website
										</span>
										<span className="text-sm">
											<a
												className="text-blue-500 break-all"
												target="_blank"
												href={entity.attributes.websiteUrl}
												rel="noreferrer"
											>
												{entity.attributes.websiteUrl}
											</a>
										</span>
									</div>
								)}
								{entity?.attributes?.foundedAt && (
									<div className="w-full sm:w-1/2 mb-4">
										<span className="uppercase whitespace-nowrap block font-bold text-sm mb-1">
											Founding date
										</span>
										<span className="text-sm whitespace-nowrap">
											{dateToDisplayDateOrEmpty(
												entity.attributes.foundedAt,
												'-'
											)}
										</span>
									</div>
								)}
								{entity.attributes?.employeeCount && (
									<div className="w-full sm:w-1/2 mb-4">
										<span className="uppercase whitespace-nowrap block font-bold text-sm mb-1">
											Employee count
										</span>
										<span className="text-sm whitespace-nowrap">
											{entity.attributes.employeeCount ?? '-'}
										</span>
									</div>
								)}
								{entity.attributes?.description && (
									<div className="w-full mb-4">
										<span className="uppercase whitespace-nowrap block font-bold text-sm mb-1">
											Description
										</span>
										<span className="text-sm whitespace-pre-wrap block">
											{entity.attributes.description}
										</span>
									</div>
								)}
							</div>
						)}
					</Tab>
					<Tab
						tabKey="notes"
						title="Notes"
						isAllowed={auth.canManageChallengeNotes(challenge)}
					>
						<div className="px-4 pb-4">
							{!isEmptyNote(challenge.attributes?.note)
								? ReactHtmlParser(challenge.attributes?.note)
								: 'No notes yet'}
						</div>
					</Tab>
				</Tabs>
			)}

			<footer className="flex items-center border-t py-3 px-4 bg-white">
				<button className="bg-gray-100 border" onClick={() => onClose()}>
					Close
				</button>
				{onEdit && auth.canEditChallenge(challenge) && (
					<button
						className="bg-gray-100 ml-auto border"
						onClick={(event) => onEdit(event, challenge, selectedTab)}
					>
						Edit
					</button>
				)}
			</footer>
		</Modal>
	)
}

ChallengeDetailsModal.displayName = 'ChallengeDetailsModal'

export default ChallengeDetailsModal
